// ─────────────────────────────────────────────
//  Facility Settings — the Facility Status list.
//
//  Split out of setupPage.facility.js for the 500-line cap, the same way the
//  roster, surgeon and sheet-import panels were. Nothing else changed: this is
//  the Active / Standby / Archived lifecycle row, one per known facility.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { setFacilityStatus, deleteFacility } from "../firebase/firestore";
import {
  FACILITY_STATUSES, FACILITY_STATUS_META, resolveFacilityStatus,
} from "../domain/facilityRules";
import { useFacilities } from "../hooks/useFacilities";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// ── Facility status (all facilities, live) ────
export function FacilityStatusPanel() {
  const facilities = useFacilities();
  return (
    <div style={s.panel}>
      <div style={{ ...s.panelTitle, marginBottom: 12 }}>🟢 Facility Status</div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0, marginBottom: 14 }}>
        Active facilities appear in the facility switcher; Standby shells are held for future use; Archived facilities are hidden everywhere. The three AdventHealth facilities are permanent; custom facilities can be removed. Admin only.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {facilities.map((f) => <FacilityStatusRow key={f.id} facility={f} />)}
      </div>
    </div>
  );
}

// One row per facility. isActive/core come from the merged useFacilities list.
// Phase 5.4 (SC1): the binary Active/Inactive toggle became the
// Active / Standby / Archived triple (facilityStatus, isActive kept in sync).
function FacilityStatusRow({ facility }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const status = resolveFacilityStatus(facility);
  const meta = FACILITY_STATUS_META[status];

  const changeStatus = async (next) => {
    if (next === status) return;
    setBusy(true);
    try {
      await setFacilityStatus(facility.id, next);
      toast.success(`${facility.label} → ${FACILITY_STATUS_META[next].label}.`);
    } catch (err) {
      console.error("Change facility status failed:", err);
      toast.error("Could not change facility status. Admin rights are required.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${facility.label}" from the system? Its rooms and config are detached. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteFacility(facility.id);
      toast.info(`${facility.label} removed.`);
    } catch (err) {
      console.error("Remove facility failed:", err);
      toast.error("Could not remove facility. Admin rights are required.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...s.roomRow }}>
      <div style={{ flex: 1, fontWeight: 700 }}>
        {facility.label}
        {facility.core && (
          <span style={{ fontSize: 10, fontWeight: 600, color: color.text.muted, marginLeft: 8 }}>core</span>
        )}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 10,
        background: meta.tint, color: meta.text }}>
        {meta.label}
      </div>
      {isAdmin && (
        <select
          aria-label={`Status for ${facility.label}`}
          style={{ ...s.select, width: "auto", padding: "4px 8px", fontSize: 12 }}
          value={status}
          disabled={busy}
          onChange={(e) => changeStatus(e.target.value)}
        >
          {FACILITY_STATUSES.map((st) => (
            <option key={st} value={st}>{FACILITY_STATUS_META[st].label}</option>
          ))}
        </select>
      )}
      {isAdmin && !facility.core && (
        <button style={{ ...s.ghostBtn, color: color.accent.error }} onClick={remove} disabled={busy}>
          Remove
        </button>
      )}
    </div>
  );
}
